import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  MessageCircle, Send, Calendar, Users, FileText, Heart, 
  Plus, Trash2, Edit2, Check, X, Loader2, Sparkles, 
  Clock, MapPin, ChevronRight, Bot, Inbox, Reply, Eye, WalletCards, Download, Image as ImageIcon, Settings, Lock, Globe
} from 'lucide-react';
import axios from 'axios';
import PWAInstallGuide from '@/components/PWAInstallGuide';
import { buildSocialLink, buildVendorEmailHref, buildVendorWhatsAppHref } from '@/lib/vendorContactLinks';
import { WeddingWebsiteBuilderSection } from '@/components/WeddingWebsiteBuilderSection';
import { GuestGallerySection } from '@/components/planner/GuestGallerySection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANNER_TAB_LABELS = {
  overview: 'Overview',
  chat: 'AI Assistant',
  messages: 'Messages',
  notes: 'Notes',
  dates: 'Important Dates',
  budget: 'Budget Planner',
  timeline: 'Timeline',
  guests: 'Guest List',
  website: 'Website Builder',
  gallery: 'Guest Gallery',
  favorites: 'Compare Vendors',
  account: 'Account',
};

const PLANNER_FEATURE_TILES = [
  {
    tab: 'chat',
    title: 'AI Assistant',
    emoji: '🤖',
  },
  {
    tab: 'messages',
    title: 'Messages',
    emoji: '💬',
  },
  {
    tab: 'notes',
    title: 'Notes',
    emoji: '📝',
  },
  {
    tab: 'dates',
    title: 'Important Dates',
    emoji: '📅',
  },
  {
    tab: 'budget',
    title: 'Budget Planner',
    emoji: '💰',
  },
  {
    tab: 'timeline',
    title: 'Timeline',
    emoji: '⏳',
  },
  {
    tab: 'guests',
    title: 'Guest List',
    emoji: '👥',
  },
  {
    tab: 'website',
    title: 'Website Builder',
    emoji: '💻',
  },
  {
    tab: 'gallery',
    title: 'Guest Gallery',
    emoji: '📸',
  },
  {
    tab: 'favorites',
    title: 'Compare Vendors',
    emoji: '❤️',
  },
];

const DISCOVERY_VENDOR_CATEGORIES = [
  { label: 'Venues', query: 'Ceremony & Reception Venues' },
  { label: 'Photographers', query: 'Photographers & Videographers' },
  { label: 'Decor', query: 'Wedding Decor & Styling' },
  { label: 'Entertainment', query: 'Entertainment' },
];

const PLANNER_TILE_HINTS = {
  chat: 'Ask anything about your wedding',
  messages: 'Reply to vendors quickly',
  notes: 'Save quick planning notes',
  dates: 'Track every key wedding date',
  budget: 'Keep spending under control',
  timeline: 'Follow your wedding task plan',
  guests: 'Manage your guests',
  website: 'Create your wedding website',
  gallery: 'Collect photos from your guests',
  favorites: 'Compare your saved vendors',
};

const PLANNER_HINTS_STORAGE_KEY = 'planner-tile-hints-seen-v1';

const WEBSITE_PROGRESS_PAGE_KEYS = ['home', 'our_story', 'photos', 'wedding_party', 'qa', 'travel', 'rsvp'];

const WEBSITE_PAGE_KEY_ALIASES = {
  home: ['title'],
  our_story: ['story'],
  photos: ['photo_gallery', 'photo'],
  wedding_party: [],
  qa: [],
  travel: ['travel_page', 'text_block', 'travel'],
  rsvp: ['rsvp_page', 'rsvp'],
};

const hasText = (value) => typeof value === 'string' && value.trim().length > 0;

const hasNonEmptyGallery = (images) => Array.isArray(images) && images.some((img) => hasText(String(img || '')));

const isMeaningfulBlock = (block) => {
  if (!block || typeof block !== 'object') return false;
  const blockType = String(block.type || '').toLowerCase();
  if (blockType === 'heading') return hasText(block.title);
  if (blockType === 'text') return hasText(block.text);
  if (blockType === 'image') return hasText(block.image_url);
  if (blockType === 'gallery') return hasNonEmptyGallery(block.images);
  return false;
};

const hasMeaningfulPageContent = (pageConfig) => {
  if (!pageConfig || typeof pageConfig !== 'object') return false;
  return (
    hasText(pageConfig.text)
    || hasText(pageConfig.image_url)
    || (Array.isArray(pageConfig.blocks) && pageConfig.blocks.some(isMeaningfulBlock))
  );
};

const resolveSectionConfigForPage = (sectionConfig, pageKey) => {
  if (!sectionConfig || typeof sectionConfig !== 'object') return null;
  const keys = [pageKey, ...(WEBSITE_PAGE_KEY_ALIASES[pageKey] || [])];
  for (const key of keys) {
    if (sectionConfig[key] && typeof sectionConfig[key] === 'object') {
      return sectionConfig[key];
    }
  }
  return null;
};

const getWebsiteProgressSnapshot = (websiteData) => {
  const totalPages = WEBSITE_PROGRESS_PAGE_KEYS.length;
  if (!websiteData?.id) {
    return { completedPages: 0, totalPages };
  }

  const sectionConfig = websiteData?.v2_builder_settings?.section_config
    || websiteData?.style_settings?.section_config
    || {};

  const content = websiteData?.content || {};
  const legacyCompletionHints = {
    home: hasText(content.couple_names) || hasText(content.date) || hasText(content.location) || hasText(content.main_photo),
    our_story: hasText(content.story) || hasText(websiteData?.story_text),
    photos: hasNonEmptyGallery(content.photos) || hasNonEmptyGallery(content.story_images) || hasText(content.main_photo),
    wedding_party: false,
    qa: false,
    travel: hasText(content.travel_info) || hasText(websiteData?.travel_info_text),
    rsvp: hasText(content.photo_sharing_message) || Boolean(websiteData?.rsvp_enabled),
  };

  const completedPages = WEBSITE_PROGRESS_PAGE_KEYS.reduce((count, pageKey) => {
    const pageConfig = resolveSectionConfigForPage(sectionConfig, pageKey);
    if (hasMeaningfulPageContent(pageConfig) || legacyCompletionHints[pageKey]) {
      return count + 1;
    }
    return count;
  }, 0);

  return { completedPages, totalPages };
};

const getWebsiteStageLabel = (websiteStatus, completedPages, totalPages) => {
  if (websiteStatus === 'published') return 'Live';
  if (completedPages === 0) return 'Getting Started';
  if (completedPages >= totalPages) return 'Ready to Publish';
  return 'In Progress';
};

const getWebsiteEncouragement = (stageLabel) => {
  if (stageLabel === 'Getting Started') return 'Start building your wedding website to bring your day to life.';
  if (stageLabel === 'Ready to Publish') return "You're almost ready to share with your guests.";
  if (stageLabel === 'Live') return 'Your website is live and ready for your guests.';
  return 'Keep going — each page brings you closer to publishing.';
};

const getPlannerTileCountText = ({ tab, summary, upcomingDatesCount, unreadFromMessages }) => {
  const unreadCount = Math.max(unreadFromMessages || 0, summary.unreadConversations || 0);

  switch (tab) {
    case 'chat':
      return `${summary.chatMessages} chat${summary.chatMessages === 1 ? '' : 's'}`;
    case 'messages':
      if (unreadCount > 0) {
        return `${unreadCount} new`;
      }
      return `${summary.conversations} message${summary.conversations === 1 ? '' : 's'}`;
    case 'notes':
      return `${summary.notes} note${summary.notes === 1 ? '' : 's'}`;
    case 'budget':
      return `${summary.budgetItems} item${summary.budgetItems === 1 ? '' : 's'}`;
    case 'timeline':
      return `${summary.timelineTotal} item${summary.timelineTotal === 1 ? '' : 's'}`;
    case 'guests':
      return `${summary.guests} guest${summary.guests === 1 ? '' : 's'}`;
    case 'website':
      if (summary.websiteStatus === 'published') {
        return 'Published';
      }
      if (summary.websiteStatus === 'draft') {
        return 'Draft';
      }
      return 'Not started';
    case 'gallery':
      return `${summary.guestGalleryPhotos} photo${summary.guestGalleryPhotos === 1 ? '' : 's'}`;
    case 'dates':
      return `${upcomingDatesCount} item${upcomingDatesCount === 1 ? '' : 's'}`;
    case 'favorites':
      return `${summary.favorites} saved`;
    default:
      return '0 items';
  }
};

export default function WeddingPlannerPage() {
  const { user, token, isAuthenticated, loading: authLoading, updateUserProfile, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const workspacePanelRef = useRef(null);
  const forcePasswordChangeRequired = Boolean(user?.force_password_change || location.state?.forcePasswordChange);
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/client/auth');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!activeWorkspace || !workspacePanelRef.current || typeof window === 'undefined') return;
    const timer = window.setTimeout(() => {
      workspacePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [activeWorkspace]);

  useEffect(() => {
    if (forcePasswordChangeRequired && activeWorkspace !== 'account') {
      setActiveWorkspace('account');
    }
  }, [forcePasswordChangeRequired, activeWorkspace]);
  
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  
  if (!isAuthenticated) return null;

  const openWorkspace = (tab) => {
    if (forcePasswordChangeRequired && tab !== 'account') {
      setActiveWorkspace('account');
      return;
    }
    if (!tab || tab === 'overview' || !Object.keys(PLANNER_TAB_LABELS).includes(tab)) {
      setActiveWorkspace(null);
      return;
    }
    setActiveWorkspace(tab);
  };

  const closeWorkspace = () => {
    if (forcePasswordChangeRequired) {
      setActiveWorkspace('account');
      return;
    }
    setActiveWorkspace(null);
  };

  const renderWorkspace = () => {
    switch (activeWorkspace) {
      case 'chat':
        return <ChatSection token={token} user={user} />;
      case 'messages':
        return <MessagesSection token={token} onUnreadChange={setMessageUnreadCount} />;
      case 'notes':
        return <NotesSection token={token} />;
      case 'dates':
        return <DatesSection token={token} />;
      case 'budget':
        return <BudgetSection token={token} />;
      case 'timeline':
        return <TimelineSection token={token} />;
      case 'guests':
        return <GuestListSection token={token} />;
      case 'website':
        return <WeddingWebsiteBuilderSection token={token} />;
      case 'gallery':
        return <GuestGallerySection token={token} />;
      case 'favorites':
        return <FavoritesComparison token={token} user={user} />;
      case 'account':
        return (
          <AccountSection
            token={token}
            user={user}
            forcePasswordChangeRequired={forcePasswordChangeRequired}
            onPasswordChanged={async () => {
              updateUserProfile({ force_password_change: false });
              await refreshUser();
            }}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-stone-50 py-8" data-testid="wedding-planner-page">
      <div className="container-wedding space-y-6">
        <PlannerOverviewSection
          token={token}
          user={user}
          forcePasswordChangeRequired={forcePasswordChangeRequired}
          messageUnreadCount={messageUnreadCount}
          onOpenTab={openWorkspace}
          activeWorkspace={activeWorkspace}
          onCloseWorkspace={closeWorkspace}
          renderWorkspace={renderWorkspace}
          workspacePanelRef={workspacePanelRef}
          onUserProfileUpdated={updateUserProfile}
        />
      </div>
      
      {/* PWA Install Guide */}
      <PWAInstallGuide />
    </div>
  );
}

function PlannerOverviewSection({ token, user, forcePasswordChangeRequired, messageUnreadCount, onOpenTab, activeWorkspace, onCloseWorkspace, renderWorkspace, workspacePanelRef, onUserProfileUpdated }) {
  const [dates, setDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [showTileHints, setShowTileHints] = useState(false);
  const [progressSummary, setProgressSummary] = useState({
    chatMessages: 0,
    conversations: 0,
    unreadConversations: 0,
    notes: 0,
    budgetItems: 0,
    budgetPaid: 0,
    timelineTotal: 0,
    timelineCompleted: 0,
    guests: 0,
    guestsConfirmed: 0,
    favorites: 0,
    websiteStatus: 'not_started',
    websiteSlug: '',
    websiteLivePath: '',
    websiteLiveUrl: '',
    websiteCompletedPages: 0,
    websiteTotalPages: WEBSITE_PROGRESS_PAGE_KEYS.length,
    websiteStageLabel: 'Getting Started',
    guestGalleryPhotos: 0,
  });
  const [websiteLinkCopied, setWebsiteLinkCopied] = useState(false);
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [settingsForm, setSettingsForm] = useState({
    couple_names: user?.couple_names || user?.name || '',
    wedding_date: user?.wedding_date || '',
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    setSettingsForm((prev) => ({
      ...prev,
      couple_names: user?.couple_names || user?.name || '',
      wedding_date: user?.wedding_date || '',
      email: user?.email || '',
    }));
  }, [user?.couple_names, user?.wedding_date, user?.email, user?.name]);

  useEffect(() => {
    const loadDates = async () => {
      try {
        const response = await axios.get(`${API}/planner/dates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDates(response.data || []);
      } catch (err) {
        console.error('Failed to load planner overview dates', err);
      } finally {
        setLoadingDates(false);
      }
    };

    loadDates();
  }, [token]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const hasSeenHints = window.localStorage.getItem(PLANNER_HINTS_STORAGE_KEY) === '1';
    if (hasSeenHints) return undefined;

    setShowTileHints(true);
    const timer = window.setTimeout(() => {
      setShowTileHints(false);
      window.localStorage.setItem(PLANNER_HINTS_STORAGE_KEY, '1');
    }, 6000);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadProgressSummary = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [
          chatRes,
          messagesRes,
          notesRes,
          budgetRes,
          timelineRes,
          guestsRes,
          favoritesRes,
          websiteRes,
          guestGalleryRes,
        ] = await Promise.allSettled([
          axios.get(`${API}/planner/chat/history`, { headers }),
          axios.get(`${API}/messages`, { headers }),
          axios.get(`${API}/planner/notes`, { headers }),
          axios.get(`${API}/planner/budget`, { headers }),
          axios.get(`${API}/planner/timeline`, { headers }),
          axios.get(`${API}/planner/guests`, { headers }),
          axios.get(`${API}/favorites`, { headers }),
          axios.get(`${API}/planner/website`, { headers }),
          axios.get(`${API}/planner/guest-gallery`, { headers }),
        ]);

        const chatMessages = chatRes.status === 'fulfilled' && Array.isArray(chatRes.value.data) ? chatRes.value.data.length : 0;
        const conversations = messagesRes.status === 'fulfilled' && Array.isArray(messagesRes.value.data) ? messagesRes.value.data : [];
        const notes = notesRes.status === 'fulfilled' && Array.isArray(notesRes.value.data) ? notesRes.value.data.length : 0;

        const budgetData = budgetRes.status === 'fulfilled' ? budgetRes.value.data : null;
        const budgetItems = Array.isArray(budgetData?.items) ? budgetData.items.length : 0;
        const budgetPaid = Array.isArray(budgetData?.items)
          ? budgetData.items.filter((item) => Boolean(item?.paid)).length
          : 0;

        const timelineData = timelineRes.status === 'fulfilled' && Array.isArray(timelineRes.value.data) ? timelineRes.value.data : [];
        const guestsData = guestsRes.status === 'fulfilled' && Array.isArray(guestsRes.value.data) ? guestsRes.value.data : [];
        const favorites = favoritesRes.status === 'fulfilled' && Array.isArray(favoritesRes.value.data) ? favoritesRes.value.data.length : 0;
        const websiteData = websiteRes.status === 'fulfilled' ? websiteRes.value.data : null;
        const guestGalleryData = guestGalleryRes.status === 'fulfilled' ? guestGalleryRes.value.data : null;
        const websiteStatus = websiteData?.id ? (websiteData?.is_published ? 'published' : 'draft') : 'not_started';
        const websiteProgress = getWebsiteProgressSnapshot(websiteData);
        const websiteStageLabel = getWebsiteStageLabel(
          websiteStatus,
          websiteProgress.completedPages,
          websiteProgress.totalPages,
        );
        const websiteSlug = hasText(websiteData?.slug) ? websiteData.slug : '';
        const websiteLivePath = websiteSlug ? `/weddings/${websiteSlug}` : '';
        const websiteLiveUrl = hasText(websiteData?.live_url) ? websiteData.live_url : websiteLivePath;

        const guestsConfirmed = guestsData.filter((guest) => {
          if (guest?.rsvp_status) return guest.rsvp_status === 'yes';
          return guest?.rsvp === 'confirmed';
        }).length;

        setProgressSummary({
          chatMessages,
          conversations: conversations.length,
          unreadConversations: conversations.filter((conversation) => conversation?.status === 'replied').length,
          notes,
          budgetItems,
          budgetPaid,
          timelineTotal: timelineData.length,
          timelineCompleted: timelineData.filter((task) => Boolean(task?.completed)).length,
          guests: guestsData.length,
          guestsConfirmed,
          favorites,
          websiteStatus,
          websiteSlug,
          websiteLivePath,
          websiteLiveUrl,
          websiteCompletedPages: websiteProgress.completedPages,
          websiteTotalPages: websiteProgress.totalPages,
          websiteStageLabel,
          guestGalleryPhotos: Array.isArray(guestGalleryData?.photos) ? guestGalleryData.photos.length : 0,
        });
      } catch (error) {
        console.error('Failed to load planner progress summary', error);
      }
    };

    loadProgressSummary();
  }, [token]);

  const futureDates = dates
    .filter((date) => date.date && new Date(date.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const weddingDate = futureDates.find((date) => /wedding|ceremony|marriage/i.test(date.title || '')) || futureDates[0];
  const upcomingDatesCount = futureDates.length;
  const today = new Date(new Date().toDateString());
  const targetDate = weddingDate?.date ? new Date(weddingDate.date) : null;
  const countdownDays = targetDate ? Math.max(0, Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24))) : null;
  const coupleNames = user?.couple_names?.trim() || user?.name?.trim() || 'Your Wedding Dashboard';

  const dismissTileHints = () => {
    if (!showTileHints || typeof window === 'undefined') return;
    setShowTileHints(false);
    window.localStorage.setItem(PLANNER_HINTS_STORAGE_KEY, '1');
  };

  const websiteProgressPercent = progressSummary.websiteTotalPages > 0
    ? Math.round((progressSummary.websiteCompletedPages / progressSummary.websiteTotalPages) * 100)
    : 0;
  const websiteStatusLabel = progressSummary.websiteStatus === 'published'
    ? 'Live'
    : progressSummary.websiteStatus === 'draft'
      ? 'Draft / Not Published'
      : 'Draft / Not Published';
  const websiteEncouragement = getWebsiteEncouragement(progressSummary.websiteStageLabel);

  const handleCopyWebsiteLink = async () => {
    if (!progressSummary.websiteLiveUrl || typeof window === 'undefined') return;
    const absoluteLink = progressSummary.websiteLiveUrl;
    try {
      if (window.navigator?.clipboard?.writeText) {
        await window.navigator.clipboard.writeText(absoluteLink);
      } else {
        window.prompt('Copy your website link', absoluteLink);
      }
      setWebsiteLinkCopied(true);
      window.setTimeout(() => setWebsiteLinkCopied(false), 2000);
    } catch (error) {
      window.prompt('Copy your website link', absoluteLink);
    }
  };

  const handleSettingsFieldChange = (field, value) => {
    setSettingsForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAccountSettings = async () => {
    setSettingsError('');
    setSettingsSuccess('');

    if (!settingsForm.couple_names.trim()) {
      setSettingsError('Couple names are required.');
      return;
    }

    setSettingsSaving(true);
    try {
      const response = await axios.put(
        `${API}/auth/me`,
        {
          couple_names: settingsForm.couple_names.trim(),
          wedding_date: settingsForm.wedding_date,
          email: settingsForm.email.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      onUserProfileUpdated?.(response.data);
      setSettingsSuccess('Account settings saved.');
    } catch (error) {
      setSettingsError(error?.response?.data?.detail || 'Failed to save account settings.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setSettingsError('');
    setSettingsSuccess('');

    if (!settingsForm.current_password || !settingsForm.new_password) {
      setSettingsError('Please enter both current and new password.');
      return;
    }
    if (settingsForm.new_password !== settingsForm.confirm_password) {
      setSettingsError('New password and confirm password do not match.');
      return;
    }

    setPasswordSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: settingsForm.current_password,
          new_password: settingsForm.new_password,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSettingsSuccess('Password updated successfully.');
      setSettingsForm((prev) => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
    } catch (error) {
      setSettingsError(error?.response?.data?.detail || 'Failed to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="planner-overview-layout">
      {forcePasswordChangeRequired && (
        <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="planner-force-password-change-alert">
          For security, please change your temporary password before using other planner tools.
        </div>
      )}
      <section className="rounded-sm border border-[#D4AF37]/30 bg-[#fffaf0] p-5" data-testid="planner-overview-countdown-card">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8f6b00] mb-2" data-testid="planner-overview-countdown-kicker">Countdown</p>
        <p className="text-3xl font-semibold text-stone-900" data-testid="planner-overview-countdown-days">
          {loadingDates ? '...' : countdownDays !== null ? `${countdownDays} day${countdownDays === 1 ? '' : 's'}` : 'Not set'}
        </p>
        <p className="text-sm text-stone-600 mt-1" data-testid="planner-overview-countdown-caption">
          {weddingDate ? `until ${weddingDate.title}` : 'Set your wedding date in Important Dates'}
        </p>
        {loadingDates ? (
          <p className="text-sm text-stone-500 mt-2" data-testid="planner-overview-date-loading">Loading wedding date...</p>
        ) : weddingDate ? (
          <p className="text-sm text-stone-700 mt-2" data-testid="planner-overview-wedding-date">
            {new Date(weddingDate.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        ) : (
          <button
            onClick={() => onOpenTab('dates')}
            className="mt-2 text-sm font-medium text-[#5C0120] hover:underline"
            data-testid="planner-overview-add-date-link"
          >
            Add your wedding date
          </button>
        )}
      </section>

      <section className="rounded-sm border border-stone-200 bg-white p-6 md:p-8 card-shadow" data-testid="planner-overview-hero-section">
        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#5C0120]" data-testid="planner-overview-kicker">
            Couples Dashboard
          </p>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center" data-testid="planner-overview-hero-icon-wrap">
              <Sparkles className="h-6 w-6 text-[#D4AF37]" data-testid="planner-overview-hero-icon" />
            </div>
            <h1 className="heading-lg" data-testid="planner-overview-couple-names">{coupleNames}</h1>
          </div>
          <p className="text-stone-600" data-testid="planner-overview-intro-copy">
            Open any planning feature below and keep your website steps at the bottom.
          </p>
        </div>
      </section>

      <section className="space-y-4" data-testid="planner-feature-grid-section">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold text-stone-900" data-testid="planner-feature-grid-title">Planning Tools</h2>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => {
                setSettingsError('');
                setSettingsSuccess('');
                setIsSettingsPanelOpen(true);
              }}
              className="border-stone-300 text-stone-700 hover:border-[#5C0120] hover:text-[#5C0120]"
              data-testid="planner-open-settings-panel-button"
            >
              <Settings className="h-4 w-4" />
            </Button>
            <details className="text-sm" data-testid="planner-secondary-actions-dropdown">
              <summary className="cursor-pointer font-medium text-[#5C0120]" data-testid="planner-secondary-actions-toggle">More options</summary>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="planner-secondary-actions-content">
              <Button type="button" variant="outline" onClick={() => onOpenTab('dates')} data-testid="planner-open-dates-inline-button">Open important dates</Button>
            </div>
            </details>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="planner-feature-grid">
          {PLANNER_FEATURE_TILES.map((tile) => {
            const tileCountText = getPlannerTileCountText({
              tab: tile.tab,
              summary: progressSummary,
              upcomingDatesCount,
              unreadFromMessages: messageUnreadCount,
            });
            const isActive = activeWorkspace === tile.tab;
            return (
              <button
                key={tile.tab}
                onClick={() => {
                  dismissTileHints();
                  onOpenTab(tile.tab);
                }}
                className={`group flex min-h-[112px] flex-col items-center justify-center rounded-sm border bg-white p-2 text-center transition-all duration-200 card-shadow ${isActive ? 'border-[#5C0120] ring-1 ring-[#5C0120]/20' : 'border-stone-100 hover:border-[#D4AF37]'}`}
                data-testid={`planner-feature-tile-${tile.tab}`}
              >
                <span className="text-2xl leading-none mb-2" data-testid={`planner-feature-emoji-${tile.tab}`}>{tile.emoji}</span>
                <h3 className={`text-xs text-center font-medium leading-tight ${isActive ? 'text-[#5C0120]' : 'text-stone-700 group-hover:text-[#5C0120]'}`} data-testid={`planner-feature-label-${tile.tab}`}>
                  {tile.title}
                </h3>
                <p className="mt-1 text-[11px] text-stone-500" data-testid={`planner-feature-progress-${tile.tab}`}>
                  {tileCountText}
                </p>
                {showTileHints && PLANNER_TILE_HINTS[tile.tab] && (
                  <p className="mt-1 text-[10px] leading-tight text-[#8f6b00]/80" data-testid={`planner-feature-hint-${tile.tab}`}>
                    {PLANNER_TILE_HINTS[tile.tab]}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-4" data-testid="planner-vendor-feature-slot">
        {activeWorkspace ? (
          <section
            ref={workspacePanelRef}
            className="rounded-sm border border-stone-200 bg-white p-4 md:p-6 card-shadow"
            data-testid="planner-inline-workspace-panel"
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 pb-4" data-testid="planner-inline-workspace-header">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5C0120]" data-testid="planner-inline-workspace-kicker">Now editing</p>
                <h2 className="text-xl font-semibold text-stone-900" data-testid="planner-inline-workspace-title">{PLANNER_TAB_LABELS[activeWorkspace]}</h2>
              </div>
              <Button
                variant="outline"
                onClick={onCloseWorkspace}
                className="border-stone-300 text-stone-700 hover:border-[#5C0120] hover:text-[#5C0120]"
                data-testid="planner-inline-workspace-close-button"
              >
                <ChevronRight className="mr-2 h-4 w-4 rotate-180" />
                Back to Dashboard
              </Button>
            </div>
            <div data-testid={`planner-inline-workspace-content-${activeWorkspace}`}>
              {renderWorkspace()}
            </div>
          </section>
        ) : (
          <PlannerDiscoverySection />
        )}
      </section>

      <section className="rounded-sm border border-stone-200 bg-stone-50/80 p-5 space-y-4" data-testid="planner-website-journey-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5C0120]" data-testid="planner-website-stage-label">Build your website step by step</p>
            <p className="text-sm text-stone-700" data-testid="planner-website-progress-numeric">
              {progressSummary.websiteCompletedPages} of {progressSummary.websiteTotalPages} pages completed
            </p>
          </div>
          <span className="rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-stone-700" data-testid="planner-website-publish-status-pill">
            {websiteStatusLabel}
          </span>
        </div>

        <div className="h-2 w-full rounded-full bg-stone-200" data-testid="planner-website-progress-track">
          <div
            className="h-full rounded-full bg-[#D4AF37] transition-[width] duration-500"
            style={{ width: `${Math.max(6, websiteProgressPercent)}%` }}
            data-testid="planner-website-progress-fill"
          />
        </div>

        <p className="text-sm text-stone-600" data-testid="planner-website-encouragement-copy">{websiteEncouragement}</p>

        <Button
          onClick={() => onOpenTab('website')}
          className="w-full md:w-auto bg-[#5C0120] hover:bg-[#5C0120]/90 text-white px-6 py-6 text-base font-semibold"
          data-testid="planner-primary-continue-website-button"
        >
          Continue Building Your Wedding Website
        </Button>

        <div className="flex flex-wrap items-center gap-2" data-testid="planner-website-journey-steps">
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${activeWorkspace === 'website' ? 'bg-[#5C0120] text-white' : 'bg-white border border-stone-200 text-stone-700'}`} data-testid="planner-journey-step-build">1. Build pages</span>
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${activeWorkspace === 'website' ? 'bg-[#D4AF37]/20 text-[#5C0120]' : 'bg-white border border-stone-200 text-stone-700'}`} data-testid="planner-journey-step-preview">2. Preview</span>
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${progressSummary.websiteStageLabel === 'Ready to Publish' || progressSummary.websiteStatus === 'published' ? 'bg-green-100 text-green-700' : 'bg-white border border-stone-200 text-stone-700'}`} data-testid="planner-journey-step-publish">3. Publish</span>
        </div>
      </section>

      <section className="rounded-sm border border-stone-200 bg-white p-5 space-y-4" data-testid="planner-publish-status-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-stone-900" data-testid="planner-publish-status-title">Website Publish Status</h2>
            <p className="text-sm text-stone-600" data-testid="planner-publish-status-subtitle">Know exactly where you are before sharing with guests.</p>
          </div>
          <p className="text-sm font-medium text-stone-700" data-testid="planner-publish-status-text">{websiteStatusLabel}</p>
        </div>

        {progressSummary.websiteStatus === 'published' && progressSummary.websiteLivePath ? (
          <div className="space-y-3" data-testid="planner-publish-live-state">
            <p className="text-sm text-stone-700 break-all" data-testid="planner-publish-live-link">
              <Globe className="inline h-4 w-4 mr-1" />
              {progressSummary.websiteLiveUrl || progressSummary.websiteLivePath}
            </p>
            <div className="flex flex-wrap items-center gap-2" data-testid="planner-publish-live-actions">
              <a
                href={progressSummary.websiteLiveUrl || progressSummary.websiteLivePath}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-sm bg-[#5C0120] px-4 py-2 text-sm font-medium text-white hover:bg-[#5C0120]/90"
                data-testid="planner-view-your-website-button"
              >
                View your website
              </a>
              <Button
                type="button"
                variant="outline"
                onClick={handleCopyWebsiteLink}
                className="border-stone-300 text-stone-700 hover:border-[#5C0120] hover:text-[#5C0120]"
                data-testid="planner-copy-website-link-button"
              >
                {websiteLinkCopied ? 'Link copied' : 'Copy link'}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-stone-600" data-testid="planner-publish-draft-copy">
            Finish your pages, preview your website, then publish when you are ready.
          </p>
        )}
      </section>

      {isSettingsPanelOpen && (
        <div className="fixed inset-0 z-40 bg-black/45 px-4 py-6" data-testid="planner-settings-panel-overlay">
          <div className="mx-auto max-w-2xl rounded-sm border border-stone-200 bg-white p-5 md:p-6 shadow-xl" data-testid="planner-settings-panel-modal">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold text-stone-900" data-testid="planner-settings-panel-title">Account Settings</h3>
                <p className="text-sm text-stone-600" data-testid="planner-settings-panel-subtitle">Update your couple profile and login details.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsSettingsPanelOpen(false)}
                data-testid="planner-settings-panel-close-button"
              >
                Close
              </Button>
            </div>

            <div className="space-y-4" data-testid="planner-settings-profile-fields">
              <div>
                <Label htmlFor="planner-settings-couple-names">Couple Names</Label>
                <Input
                  id="planner-settings-couple-names"
                  value={settingsForm.couple_names}
                  onChange={(event) => handleSettingsFieldChange('couple_names', event.target.value)}
                  data-testid="planner-settings-couple-names-input"
                />
              </div>
              <div>
                <Label htmlFor="planner-settings-wedding-date">Wedding Date</Label>
                <Input
                  id="planner-settings-wedding-date"
                  type="date"
                  value={settingsForm.wedding_date || ''}
                  onChange={(event) => handleSettingsFieldChange('wedding_date', event.target.value)}
                  data-testid="planner-settings-wedding-date-input"
                />
              </div>
              <div>
                <Label htmlFor="planner-settings-email">Email</Label>
                <Input
                  id="planner-settings-email"
                  type="email"
                  value={settingsForm.email}
                  onChange={(event) => handleSettingsFieldChange('email', event.target.value)}
                  data-testid="planner-settings-email-input"
                />
              </div>
              <Button
                type="button"
                onClick={handleSaveAccountSettings}
                disabled={settingsSaving}
                className="bg-[#5C0120] hover:bg-[#5C0120]/90"
                data-testid="planner-settings-save-profile-button"
              >
                {settingsSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>

            <div className="mt-6 space-y-4 border-t border-stone-100 pt-4" data-testid="planner-settings-password-fields">
              <p className="text-sm font-semibold text-stone-900">Change Password</p>
              <div>
                <Label htmlFor="planner-settings-current-password">Current Password</Label>
                <Input
                  id="planner-settings-current-password"
                  type="password"
                  value={settingsForm.current_password}
                  onChange={(event) => handleSettingsFieldChange('current_password', event.target.value)}
                  data-testid="planner-settings-current-password-input"
                />
              </div>
              <div>
                <Label htmlFor="planner-settings-new-password">New Password</Label>
                <Input
                  id="planner-settings-new-password"
                  type="password"
                  value={settingsForm.new_password}
                  onChange={(event) => handleSettingsFieldChange('new_password', event.target.value)}
                  data-testid="planner-settings-new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="planner-settings-confirm-password">Confirm New Password</Label>
                <Input
                  id="planner-settings-confirm-password"
                  type="password"
                  value={settingsForm.confirm_password}
                  onChange={(event) => handleSettingsFieldChange('confirm_password', event.target.value)}
                  data-testid="planner-settings-confirm-password-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleChangePassword}
                disabled={passwordSaving}
                data-testid="planner-settings-change-password-button"
              >
                {passwordSaving ? 'Updating...' : 'Change Password'}
              </Button>
            </div>

            {(settingsError || settingsSuccess) && (
              <p
                className={`mt-4 text-sm ${settingsError ? 'text-red-600' : 'text-emerald-700'}`}
                data-testid="planner-settings-feedback-text"
              >
                {settingsError || settingsSuccess}
              </p>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function PlannerDiscoverySection() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDiscoveryVendors = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all(
          DISCOVERY_VENDOR_CATEGORIES.map(async (category) => {
            const response = await axios.get(`${API}/vendors`, {
              params: { category: category.query }
            });

            return (response.data || []).slice(0, 5).map((vendor) => ({
              ...vendor,
              discoveryCategory: category.label,
            }));
          })
        );

        const deduped = [];
        const seen = new Set();

        responses.flat().forEach((vendor) => {
          const key = vendor.id || vendor.slug || vendor.name;
          if (!key || seen.has(key)) return;
          seen.add(key);
          deduped.push(vendor);
        });

        setVendors(deduped);
      } catch (error) {
        console.error('Failed to load discovery vendors', error);
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };

    loadDiscoveryVendors();
  }, []);

  return (
    <section className="space-y-4" data-testid="planner-discovery-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-stone-900" data-testid="planner-discovery-title">Discover Vendors</h2>
          <p className="text-sm text-stone-600" data-testid="planner-discovery-subtitle">Venues, photographers, decor, and entertainment picks for your shortlist.</p>
        </div>
        <Link to="/vendors" className="text-sm font-medium text-[#5C0120] hover:underline" data-testid="planner-discovery-browse-all-link">
          Browse all vendors
        </Link>
      </div>

      {loading ? (
        <div className="rounded-sm border border-stone-200 bg-white p-10 text-center" data-testid="planner-discovery-loading-state">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" />
        </div>
      ) : vendors.length === 0 ? (
        <div className="rounded-sm border border-stone-200 bg-white p-10 text-center" data-testid="planner-discovery-empty-state">
          <p className="text-stone-600">No vendors available right now. Browse the full directory to explore more.</p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-2" data-testid="planner-discovery-scroll-container">
          <div className="flex min-w-max gap-4" data-testid="planner-discovery-scroll-row">
            {vendors.map((vendor) => (
              <article
                key={vendor.id || vendor.slug || vendor.name}
                className="w-[270px] shrink-0 rounded-sm border border-stone-200 bg-white overflow-hidden card-shadow"
                data-testid={`planner-discovery-vendor-card-${(vendor.id || vendor.slug || vendor.name || '').toString().replace(/[^a-zA-Z0-9-]/g, '-')}`}
              >
                <div className="aspect-[4/3] bg-stone-100">
                  {vendor.cover_image || vendor.images?.[0] ? (
                    <img
                      src={vendor.cover_image || vendor.images?.[0]}
                      alt={vendor.name}
                      className="h-full w-full object-cover"
                      data-testid={`planner-discovery-vendor-image-${vendor.id || vendor.slug || vendor.name}`}
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-stone-400 text-sm" data-testid={`planner-discovery-vendor-image-fallback-${vendor.id || vendor.slug || vendor.name}`}>
                      No image
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5C0120]" data-testid={`planner-discovery-vendor-category-${vendor.id || vendor.slug || vendor.name}`}>
                    {vendor.discoveryCategory}
                  </p>
                  <h3 className="font-semibold text-stone-900 line-clamp-2" data-testid={`planner-discovery-vendor-name-${vendor.id || vendor.slug || vendor.name}`}>
                    {vendor.name}
                  </h3>
                  <p className="text-sm text-stone-600 line-clamp-2" data-testid={`planner-discovery-vendor-description-${vendor.id || vendor.slug || vendor.name}`}>
                    {vendor.description || 'Explore this trusted supplier for your wedding plans.'}
                  </p>
                  <Link
                    to={`/vendors/${vendor.slug || vendor.id}`}
                    className="inline-flex items-center text-sm font-medium text-[#5C0120] hover:underline"
                    data-testid={`planner-discovery-vendor-link-${vendor.id || vendor.slug || vendor.name}`}
                  >
                    View profile <ChevronRight className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// AI Chat Section
function ChatSection({ token, user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [includeImage, setIncludeImage] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  useEffect(() => {
    loadChatHistory();
  }, []);
  
  useEffect(() => {
    // Scroll within the chat container only, not the whole page
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/planner/chat/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (err) {
      console.error('Failed to load chat history');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    
    const userMessage = { role: 'user', content: input, timestamp: new Date().toISOString(), request_image: includeImage };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/planner/chat`, 
        { message: input, request_image: includeImage },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        image_url: response.data.image_url,
        timestamp: new Date().toISOString()
      }]);
      setIncludeImage(false);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, the planner is taking longer than expected right now. Please try again in a moment.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  const quickPrompts = [
    "Help me create a guest list",
    "Suggest a photography shot list",
    "Help me plan table seating",
    "What's unique about Gibraltar weddings?",
    "Create a wedding day timeline"
  ];
  
  return (
    <div className="bg-white rounded-sm border border-stone-200 overflow-hidden" style={{ height: '600px' }}>
      <div className="flex flex-col h-full">
        {/* Chat Header */}
        <div className="p-4 border-b border-stone-100 bg-gradient-to-r from-[#5C0120] to-[#5C0120]/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Gibraltar Wedding Expert</h3>
              <p className="text-white/70 text-sm">ChatGPT-style planning help with optional inspiration images</p>
            </div>
          </div>
        </div>
        
        {/* Messages */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <Bot className="h-12 w-12 mx-auto text-stone-300 mb-4" />
              <h4 className="font-medium text-stone-700 mb-2">How can I help with your wedding?</h4>
              <p className="text-stone-500 text-sm mb-6">Ask me anything about planning your Gibraltar wedding</p>
              <div className="flex flex-wrap justify-center gap-2">
                {quickPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(prompt)}
                    className="text-sm px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-stone-600 transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  msg.role === 'user' 
                    ? 'bg-[#5C0120] text-white' 
                    : 'bg-stone-100 text-stone-800'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  {msg.image_url && (
                    <img
                      src={msg.image_url}
                      alt="Wedding inspiration"
                      className="mt-3 rounded-lg border border-stone-200 max-h-72 w-full object-cover"
                      data-testid="planner-chat-generated-image"
                    />
                  )}
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-stone-100 p-3 rounded-lg">
                <Loader2 className="h-5 w-5 animate-spin text-stone-500" />
              </div>
            </div>
          )}
        </div>
        
        {/* Input */}
        <form onSubmit={sendMessage} className="p-4 border-t border-stone-100">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => setIncludeImage((prev) => !prev)}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${includeImage ? 'bg-[#D4AF37] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
              data-testid="planner-chat-image-toggle"
            >
              <ImageIcon className="h-3.5 w-3.5" />
              {includeImage ? 'Image request on' : 'Add inspiration image'}
            </button>
            <span className="text-xs text-stone-500">Use this when you want moodboard-style inspiration returned in the chat.</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about budgets, timelines, vendors, styling ideas..."
              className="flex-1"
              disabled={loading}
              data-testid="planner-chat-input"
            />
            <Button type="submit" disabled={loading || !input.trim()} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90" data-testid="planner-chat-submit">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Notes Section
function NotesSection({ token }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState({ title: '', content: '' });
  const [editingId, setEditingId] = useState(null);
  
  useEffect(() => {
    loadNotes();
  }, []);
  
  const loadNotes = async () => {
    try {
      const response = await axios.get(`${API}/planner/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(response.data);
    } catch (err) {
      console.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };
  
  const saveNote = async () => {
    if (!newNote.title.trim()) return;
    
    try {
      if (editingId) {
        await axios.put(`${API}/planner/notes/${editingId}`, newNote, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/planner/notes`, newNote, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setNewNote({ title: '', content: '' });
      setEditingId(null);
      loadNotes();
    } catch (err) {
      alert('Failed to save note');
    }
  };
  
  const deleteNote = async (id) => {
    if (!confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/planner/notes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadNotes();
    } catch (err) {
      alert('Failed to delete note');
    }
  };
  
  const editNote = (note) => {
    setNewNote({ title: note.title, content: note.content });
    setEditingId(note.id);
  };
  
  return (
    <div className="space-y-6">
      {/* Add Note Form */}
      <div className="bg-white rounded-sm border border-stone-200 p-6">
        <h3 className="font-semibold mb-4">{editingId ? 'Edit Note' : 'Add New Note'}</h3>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={newNote.title}
              onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
              placeholder="Note title..."
              className="mt-1"
            />
          </div>
          <div>
            <Label>Content</Label>
            <textarea
              value={newNote.content}
              onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
              placeholder="Write your notes here..."
              className="w-full mt-1 p-3 border border-stone-200 rounded-sm min-h-[120px] resize-none focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={saveNote} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90">
              {editingId ? 'Update Note' : 'Save Note'}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={() => { setEditingId(null); setNewNote({ title: '', content: '' }); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Notes List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
          </div>
        ) : notes.length === 0 ? (
          <div className="col-span-full text-center py-8 text-stone-500">
            <FileText className="h-12 w-12 mx-auto mb-2 text-stone-300" />
            <p>No notes yet. Start adding your wedding notes above!</p>
          </div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="bg-white rounded-sm border border-stone-200 p-4">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold">{note.title}</h4>
                <div className="flex gap-1">
                  <button onClick={() => editNote(note)} className="p-1 text-stone-400 hover:text-[#5C0120]">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => deleteNote(note.id)} className="p-1 text-stone-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-stone-600 whitespace-pre-wrap">{note.content}</p>
              <p className="text-xs text-stone-400 mt-3">
                {new Date(note.created_at).toLocaleDateString()}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Important Dates Section
function DatesSection({ token }) {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState({ title: '', date: '', time: '', location: '', notes: '' });
  const [editingId, setEditingId] = useState(null);
  
  useEffect(() => {
    loadDates();
  }, []);
  
  const loadDates = async () => {
    try {
      const response = await axios.get(`${API}/planner/dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDates(response.data);
    } catch (err) {
      console.error('Failed to load dates');
    } finally {
      setLoading(false);
    }
  };
  
  const saveDate = async () => {
    if (!newDate.title.trim() || !newDate.date) return;
    
    try {
      if (editingId) {
        await axios.put(`${API}/planner/dates/${editingId}`, newDate, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/planner/dates`, newDate, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setNewDate({ title: '', date: '', time: '', location: '', notes: '' });
      setEditingId(null);
      loadDates();
    } catch (err) {
      alert('Failed to save date');
    }
  };

  const editDate = (date) => {
    setNewDate({
      title: date.title,
      date: date.date,
      time: date.time || '',
      location: date.location || '',
      notes: date.notes || '',
    });
    setEditingId(date.id);
  };
  
  const deleteDate = async (id) => {
    if (!confirm('Delete this appointment?')) return;
    try {
      await axios.delete(`${API}/planner/dates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadDates();
    } catch (err) {
      alert('Failed to delete date');
    }
  };
  
  // Sort dates by upcoming
  const sortedDates = [...dates].sort((a, b) => new Date(a.date) - new Date(b.date));
  const upcomingDates = sortedDates.filter(d => new Date(d.date) >= new Date());
  const pastDates = sortedDates.filter(d => new Date(d.date) < new Date());
  
  return (
    <div className="space-y-6">
      {/* Add Date Form */}
      <div className="bg-white rounded-sm border border-stone-200 p-6">
        <h3 className="font-semibold mb-4">{editingId ? 'Edit Important Date' : 'Add Important Date / Appointment'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Event Title *</Label>
            <Input
              value={newDate.title}
              onChange={(e) => setNewDate({ ...newDate, title: e.target.value })}
              placeholder="e.g., Venue tasting, Dress fitting..."
              className="mt-1"
            />
          </div>
          <div>
            <Label>Date *</Label>
            <Input
              type="date"
              value={newDate.date}
              onChange={(e) => setNewDate({ ...newDate, date: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Time</Label>
            <Input
              type="time"
              value={newDate.time}
              onChange={(e) => setNewDate({ ...newDate, time: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Location</Label>
            <Input
              value={newDate.location}
              onChange={(e) => setNewDate({ ...newDate, location: e.target.value })}
              placeholder="e.g., The Rock Hotel, Main Street..."
              className="mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Input
              value={newDate.notes}
              onChange={(e) => setNewDate({ ...newDate, notes: e.target.value })}
              placeholder="Any additional notes..."
              className="mt-1"
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button onClick={saveDate} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90" data-testid="planner-date-save-button">
            <Plus className="h-4 w-4 mr-2" /> {editingId ? 'Update Date' : 'Add Date'}
          </Button>
          {editingId && (
            <Button variant="outline" onClick={() => { setEditingId(null); setNewDate({ title: '', date: '', time: '', location: '', notes: '' }); }}>
              Cancel
            </Button>
          )}
        </div>
      </div>
      
      {/* Upcoming Dates */}
      <div>
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-[#D4AF37]" />
          Upcoming ({upcomingDates.length})
        </h3>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
          </div>
        ) : upcomingDates.length === 0 ? (
          <div className="bg-white rounded-sm border border-stone-200 p-8 text-center text-stone-500">
            <Calendar className="h-12 w-12 mx-auto mb-2 text-stone-300" />
            <p>No upcoming dates. Add your wedding appointments above!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingDates.map((date) => (
              <div key={date.id} className="bg-white rounded-sm border border-stone-200 p-4 flex items-center gap-4">
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-sm flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#D4AF37]">
                    {new Date(date.date).getDate()}
                  </span>
                  <span className="text-xs text-[#D4AF37] uppercase">
                    {new Date(date.date).toLocaleDateString('en-GB', { month: 'short' })}
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold">{date.title}</h4>
                  <div className="flex flex-wrap gap-3 text-sm text-stone-500 mt-1">
                    {date.time && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {date.time}
                      </span>
                    )}
                    {date.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {date.location}
                      </span>
                    )}
                  </div>
                  {date.notes && <p className="text-sm text-stone-600 mt-1">{date.notes}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => editDate(date)} className="p-2 text-stone-400 hover:text-[#5C0120]" data-testid={`planner-date-edit-${date.id}`}>
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => deleteDate(date.id)} className="p-2 text-stone-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Past Dates */}
      {pastDates.length > 0 && (
        <div>
          <h3 className="font-semibold mb-4 text-stone-500">Past Dates ({pastDates.length})</h3>
          <div className="space-y-2 opacity-60">
            {pastDates.map((date) => (
              <div key={date.id} className="bg-white rounded-sm border border-stone-200 p-3 flex items-center gap-3">
                <span className="text-sm text-stone-500 w-24">
                  {new Date(date.date).toLocaleDateString('en-GB')}
                </span>
                <span className="flex-1">{date.title}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => editDate(date)} className="p-1 text-stone-400 hover:text-[#5C0120]">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => deleteDate(date.id)} className="p-1 text-stone-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Guest List Section
function GuestListSection({ token }) {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGuest, setNewGuest] = useState({ name: '', email: '', phone: '', group: 'Friends', rsvp_status: 'pending', dietary: 'None', additional_guests_count: 0 });
  const [editingGuestId, setEditingGuestId] = useState(null);
  
  const groups = ['Family - Bride', 'Family - Groom', 'Friends', 'Work Colleagues', 'Other'];
  const rsvpStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];
  const getRsvpStatus = (guest) => {
    if (guest.rsvp_status) return guest.rsvp_status;
    if (guest.rsvp === 'confirmed') return 'yes';
    if (guest.rsvp === 'declined') return 'no';
    return 'pending';
  };
  
  const loadGuests = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/planner/guests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(response.data);
    } catch (err) {
      console.error('Failed to load guests');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    loadGuests();

    const refreshInterval = window.setInterval(() => {
      loadGuests(true);
    }, 4000);

    const handleStorageRefresh = (event) => {
      if (event.key === 'planner_guest_refresh_signal') {
        loadGuests(true);
      }
    };

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') {
        loadGuests(true);
      }
    };

    window.addEventListener('storage', handleStorageRefresh);
    document.addEventListener('visibilitychange', handleVisibilityRefresh);

    return () => {
      window.clearInterval(refreshInterval);
      window.removeEventListener('storage', handleStorageRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
    };
  }, [loadGuests]);
  
  const saveGuest = async () => {
    if (!newGuest.name.trim()) return;
    
    try {
      if (editingGuestId) {
        await axios.put(`${API}/planner/guests/${editingGuestId}`, newGuest, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/planner/guests`, newGuest, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setNewGuest({ name: '', email: '', phone: '', group: 'Friends', rsvp_status: 'pending', dietary: 'None', additional_guests_count: 0 });
      setEditingGuestId(null);
      loadGuests();
    } catch (err) {
      alert('Failed to save guest');
    }
  };

  const editGuest = (guest) => {
    setNewGuest({
      name: guest.name,
      email: guest.email || '',
      phone: guest.phone || '',
      group: guest.group || 'Friends',
      rsvp_status: getRsvpStatus(guest),
      dietary: guest.dietary || 'None',
      additional_guests_count: Number.isInteger(guest.additional_guests_count)
        ? guest.additional_guests_count
        : (guest.plus_one ? 1 : 0),
    });
    setEditingGuestId(guest.id);
  };
  
  const updateGuestRsvp = async (id, rsvp_status) => {
    try {
      await axios.put(`${API}/planner/guests/${id}`, { rsvp_status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadGuests();
    } catch (err) {
      alert('Failed to update RSVP');
    }
  };
  
  const deleteGuest = async (id) => {
    if (!confirm('Remove this guest?')) return;
    try {
      await axios.delete(`${API}/planner/guests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadGuests();
    } catch (err) {
      alert('Failed to delete guest');
    }
  };
  
  // Stats
  const confirmed = guests
    .filter((guest) => getRsvpStatus(guest) === 'yes')
    .reduce((total, guest) => total + 1 + (Number.isInteger(guest.additional_guests_count) ? guest.additional_guests_count : (guest.plus_one ? 1 : 0)), 0);
  const declined = guests.filter((guest) => getRsvpStatus(guest) === 'no').length;
  const pending = guests.filter((guest) => getRsvpStatus(guest) === 'pending').length;
  
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-sm border border-stone-200 p-4 text-center">
          <p className="text-3xl font-bold text-[#5C0120]">{guests.length}</p>
          <p className="text-sm text-stone-500">Total Guests</p>
        </div>
        <div className="bg-white rounded-sm border border-stone-200 p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{confirmed}</p>
          <p className="text-sm text-stone-500">Confirmed</p>
        </div>
        <div className="bg-white rounded-sm border border-stone-200 p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">{pending}</p>
          <p className="text-sm text-stone-500">Pending</p>
        </div>
        <div className="bg-white rounded-sm border border-stone-200 p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{declined}</p>
          <p className="text-sm text-stone-500">Declined</p>
        </div>
      </div>
      
      {/* Add Guest Form */}
      <div className="bg-white rounded-sm border border-stone-200 p-6">
        <h3 className="font-semibold mb-4">{editingGuestId ? 'Edit Guest' : 'Add Guest'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Name *</Label>
            <Input
              value={newGuest.name}
              onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })}
              placeholder="Guest name"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={newGuest.email}
              onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })}
              placeholder="guest@email.com"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={newGuest.phone}
              onChange={(e) => setNewGuest({ ...newGuest, phone: e.target.value })}
              placeholder="Phone number"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Group</Label>
            <select
              value={newGuest.group}
              onChange={(e) => setNewGuest({ ...newGuest, group: e.target.value })}
              className="w-full mt-1 p-2 border border-stone-200 rounded-sm"
            >
              {groups.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <Label>Dietary Requirements</Label>
            <Input
              value={newGuest.dietary}
              onChange={(e) => setNewGuest({ ...newGuest, dietary: e.target.value })}
              placeholder="e.g., Vegetarian, Gluten-free"
              className="mt-1"
              data-testid="planner-guest-dietary-input"
            />
          </div>
          <div className="flex items-end">
            <div className="w-full">
              <Label>Additional Guests Count</Label>
              <Input
                type="number"
                min={0}
                value={newGuest.additional_guests_count}
                onChange={(e) => setNewGuest({ ...newGuest, additional_guests_count: Number(e.target.value || 0) })}
                className="mt-1"
                data-testid="planner-guest-additional-count-input"
              />
            </div>
          </div>
          <div className="flex items-end">
            <Button onClick={saveGuest} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90" data-testid="planner-guest-save-button">
              <Plus className="h-4 w-4 mr-2" /> {editingGuestId ? 'Update Guest' : 'Add Guest'}
            </Button>
          </div>
          {editingGuestId && (
            <div className="flex items-end">
              <Button variant="outline" onClick={() => { setEditingGuestId(null); setNewGuest({ name: '', email: '', phone: '', group: 'Friends', rsvp_status: 'pending', dietary: 'None', additional_guests_count: 0 }); }}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Guest List */}
      <div className="bg-white rounded-sm border border-stone-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-50">
            <tr>
              <th className="text-left p-3 font-medium">Name</th>
              <th className="text-left p-3 font-medium">Group</th>
              <th className="text-left p-3 font-medium">Dietary</th>
              <th className="text-left p-3 font-medium">Additional Guests</th>
              <th className="text-left p-3 font-medium">RSVP</th>
              <th className="text-left p-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin text-stone-400 mx-auto" />
                </td>
              </tr>
            ) : guests.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-stone-500">
                  <Users className="h-12 w-12 mx-auto mb-2 text-stone-300" />
                  <p>No guests yet. Start building your guest list above!</p>
                </td>
              </tr>
            ) : (
              guests.map((guest) => (
                <tr key={guest.id} className="border-t border-stone-100">
                  <td className="p-3">
                    <p className="font-medium">{guest.name}</p>
                    {guest.email && <p className="text-sm text-stone-500">{guest.email}</p>}
                    {guest.phone && <p className="text-xs text-stone-400">{guest.phone}</p>}
                  </td>
                  <td className="p-3 text-sm">
                    <p>{guest.group}</p>
                    {guest.source === 'wedding-website-rsvp' && <p className="text-xs text-stone-400">Website RSVP</p>}
                  </td>
                  <td className="p-3 text-sm">{guest.dietary || '-'}</td>
                  <td className="p-3">{Number.isInteger(guest.additional_guests_count) ? guest.additional_guests_count : (guest.plus_one ? 1 : 0)}</td>
                  <td className="p-3">
                    <select
                      value={getRsvpStatus(guest)}
                      onChange={(e) => updateGuestRsvp(guest.id, e.target.value)}
                      className={`text-sm p-1 rounded border ${
                        getRsvpStatus(guest) === 'yes' ? 'bg-green-100 text-green-700 border-green-200' :
                        getRsvpStatus(guest) === 'no' ? 'bg-red-100 text-red-700 border-red-200' :
                        'bg-amber-100 text-amber-700 border-amber-200'
                      }`}
                    >
                      {rsvpStatuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => editGuest(guest)} className="p-1 text-stone-400 hover:text-[#5C0120]" data-testid={`planner-guest-edit-${guest.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => deleteGuest(guest.id)} className="p-1 text-stone-400 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BudgetSection({ token }) {
  const [settings, setSettings] = useState({ total_budget: 0, currency: '£' });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', category: 'Venue', estimated_cost: 0, actual_cost: 0, vendor_name: '', due_date: '', notes: '', paid: false });

  useEffect(() => {
    loadBudget();
  }, []);

  const loadBudget = async () => {
    try {
      const response = await axios.get(`${API}/planner/budget`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(response.data.settings);
      setItems(response.data.items);
    } catch (err) {
      console.error('Failed to load budget', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    await axios.put(`${API}/planner/budget/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
    loadBudget();
  };

  const saveItem = async () => {
    if (!form.title.trim()) return;
    if (editingId) {
      await axios.put(`${API}/planner/budget/items/${editingId}`, form, { headers: { Authorization: `Bearer ${token}` } });
    } else {
      await axios.post(`${API}/planner/budget/items`, form, { headers: { Authorization: `Bearer ${token}` } });
    }
    setEditingId(null);
    setForm({ title: '', category: 'Venue', estimated_cost: 0, actual_cost: 0, vendor_name: '', due_date: '', notes: '', paid: false });
    loadBudget();
  };

  const editItem = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      category: item.category,
      estimated_cost: item.estimated_cost || 0,
      actual_cost: item.actual_cost || 0,
      vendor_name: item.vendor_name || '',
      due_date: item.due_date || '',
      notes: item.notes || '',
      paid: Boolean(item.paid),
    });
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this budget item?')) return;
    await axios.delete(`${API}/planner/budget/items/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
    loadBudget();
  };

  const estimatedTotal = items.reduce((sum, item) => sum + Number(item.estimated_cost || 0), 0);
  const actualTotal = items.reduce((sum, item) => sum + Number(item.actual_cost || 0), 0);
  const remaining = Number(settings.total_budget || 0) - actualTotal;

  return (
    <div className="space-y-6" data-testid="planner-budget-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-sm border border-stone-200 p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Total budget</p>
          <p className="text-3xl font-semibold text-[#5C0120]">{settings.currency}{Number(settings.total_budget || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-sm border border-stone-200 p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Planned</p>
          <p className="text-3xl font-semibold text-stone-800">{settings.currency}{estimatedTotal.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-sm border border-stone-200 p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Spent</p>
          <p className="text-3xl font-semibold text-[#D4AF37]">{settings.currency}{actualTotal.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-sm border border-stone-200 p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Remaining</p>
          <p className={`text-3xl font-semibold ${remaining >= 0 ? 'text-green-700' : 'text-red-600'}`}>{settings.currency}{remaining.toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-stone-200 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Total Wedding Budget</Label>
            <Input type="number" value={settings.total_budget} onChange={(e) => setSettings((prev) => ({ ...prev, total_budget: Number(e.target.value || 0) }))} className="mt-1" data-testid="planner-budget-total-input" />
          </div>
          <div>
            <Label>Currency</Label>
            <Input value={settings.currency} onChange={(e) => setSettings((prev) => ({ ...prev, currency: e.target.value || '£' }))} className="mt-1" />
          </div>
          <div className="flex items-end">
            <Button onClick={saveSettings} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90">Save Budget</Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-stone-200 p-6 space-y-4">
        <h3 className="font-semibold">{editingId ? 'Edit Budget Item' : 'Add Budget Item'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label>Item</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>Category</Label>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>Estimated</Label>
            <Input type="number" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: Number(e.target.value || 0) })} className="mt-1" />
          </div>
          <div>
            <Label>Actual Spend</Label>
            <Input type="number" value={form.actual_cost} onChange={(e) => setForm({ ...form, actual_cost: Number(e.target.value || 0) })} className="mt-1" />
          </div>
          <div>
            <Label>Vendor</Label>
            <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>Due Date</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-600">
          <input type="checkbox" checked={form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} /> Paid
        </label>
        <div className="flex gap-2">
          <Button onClick={saveItem} className="bg-[#5C0120] hover:bg-[#5C0120]/90" data-testid="planner-budget-item-save-button">{editingId ? 'Update Item' : 'Add Item'}</Button>
          {editingId && <Button variant="outline" onClick={() => { setEditingId(null); setForm({ title: '', category: 'Venue', estimated_cost: 0, actual_cost: 0, vendor_name: '', due_date: '', notes: '', paid: false }); }}>Cancel</Button>}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" /></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-stone-500">No budget items yet.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-stone-50">
              <tr>
                <th className="p-3 text-left font-medium">Item</th>
                <th className="p-3 text-left font-medium">Category</th>
                <th className="p-3 text-left font-medium">Estimated</th>
                <th className="p-3 text-left font-medium">Spent</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-stone-100">
                  <td className="p-3">
                    <p className="font-medium">{item.title}</p>
                    {item.vendor_name && <p className="text-xs text-stone-500">{item.vendor_name}</p>}
                  </td>
                  <td className="p-3 text-sm">{item.category}</td>
                  <td className="p-3 text-sm">{settings.currency}{Number(item.estimated_cost || 0).toLocaleString()}</td>
                  <td className="p-3 text-sm">{settings.currency}{Number(item.actual_cost || 0).toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`rounded-full px-3 py-1 text-xs ${item.paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{item.paid ? 'Paid' : 'Planned'}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => editItem(item)} className="p-1 text-stone-400 hover:text-[#5C0120]"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => deleteItem(item.id)} className="p-1 text-stone-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function TimelineSection({ token }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', phase: '', due_date: '', notes: '', completed: false, sort_order: 999 });

  useEffect(() => {
    loadTimeline();
  }, []);

  const loadTimeline = async () => {
    try {
      const response = await axios.get(`${API}/planner/timeline`, { headers: { Authorization: `Bearer ${token}` } });
      setTasks(response.data);
    } catch (err) {
      console.error('Failed to load timeline', err);
    } finally {
      setLoading(false);
    }
  };

  const saveTask = async () => {
    if (!form.title.trim()) return;
    if (editingId) {
      await axios.put(`${API}/planner/timeline/${editingId}`, form, { headers: { Authorization: `Bearer ${token}` } });
    } else {
      await axios.post(`${API}/planner/timeline`, form, { headers: { Authorization: `Bearer ${token}` } });
    }
    setEditingId(null);
    setForm({ title: '', phase: '', due_date: '', notes: '', completed: false, sort_order: 999 });
    loadTimeline();
  };

  const editTask = (task) => {
    setEditingId(task.id);
    setForm({
      title: task.title,
      phase: task.phase || '',
      due_date: task.due_date || '',
      notes: task.notes || '',
      completed: Boolean(task.completed),
      sort_order: task.sort_order || 999,
    });
  };

  const toggleTask = async (task) => {
    await axios.put(`${API}/planner/timeline/${task.id}`, {
      title: task.title,
      phase: task.phase || '',
      due_date: task.due_date || '',
      notes: task.notes || '',
      completed: !task.completed,
      sort_order: task.sort_order || 999,
    }, { headers: { Authorization: `Bearer ${token}` } });
    loadTimeline();
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this timeline task?')) return;
    await axios.delete(`${API}/planner/timeline/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
    loadTimeline();
  };

  const exportTimelinePdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Gibraltar Wedding Planning Timeline', 14, 18);
    doc.setFontSize(11);
    let y = 32;
    tasks.forEach((task, index) => {
      const line = `${index + 1}. ${task.title} ${task.phase ? `(${task.phase})` : ''} ${task.completed ? '- Completed' : ''}`;
      const split = doc.splitTextToSize(line, 180);
      doc.text(split, 14, y);
      y += split.length * 6;
      if (task.notes) {
        const notes = doc.splitTextToSize(`Notes: ${task.notes}`, 172);
        doc.text(notes, 18, y);
        y += notes.length * 6;
      }
      y += 4;
      if (y > 265) {
        doc.addPage();
        y = 20;
      }
    });
    doc.save('gibraltar-wedding-timeline.pdf');
  };

  return (
    <div className="space-y-6" data-testid="planner-timeline-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-sm border border-stone-200 p-6">
        <div>
          <h3 className="font-semibold text-lg">Gibraltar Wedding Timeline</h3>
          <p className="text-sm text-stone-500">Use the default checklist, customise it, and export your timeline as a PDF.</p>
        </div>
        <Button onClick={exportTimelinePdf} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" data-testid="planner-timeline-export-button">
          <Download className="h-4 w-4 mr-2" /> Export PDF
        </Button>
      </div>

      <div className="bg-white rounded-sm border border-stone-200 p-6 space-y-4">
        <h3 className="font-semibold">{editingId ? 'Edit Timeline Task' : 'Add Timeline Task'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label>Task</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>Planning Phase</Label>
            <Input value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} placeholder="e.g. 6 months before" className="mt-1" />
          </div>
          <div>
            <Label>Due Date</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>Display Order</Label>
            <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value || 999) })} className="mt-1" />
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={form.completed} onChange={(e) => setForm({ ...form, completed: e.target.checked })} /> Mark as completed</label>
        <div className="flex gap-2">
          <Button onClick={saveTask} className="bg-[#5C0120] hover:bg-[#5C0120]/90" data-testid="planner-timeline-save-button">{editingId ? 'Update Task' : 'Add Task'}</Button>
          {editingId && <Button variant="outline" onClick={() => { setEditingId(null); setForm({ title: '', phase: '', due_date: '', notes: '', completed: false, sort_order: 999 }); }}>Cancel</Button>}
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-sm border border-stone-200 p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" /></div>
        ) : tasks.map((task, index) => (
          <div key={task.id} className="bg-white rounded-sm border border-stone-200 p-4 flex flex-col md:flex-row md:items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#5C0120] text-white flex items-center justify-center font-semibold">{index + 1}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold">{task.title}</h4>
                {task.phase && <span className="rounded-full bg-[#D4AF37]/15 px-3 py-1 text-xs text-[#5C0120]">{task.phase}</span>}
                {task.completed && <span className="rounded-full bg-green-100 px-3 py-1 text-xs text-green-700">Completed</span>}
              </div>
              {task.due_date && <p className="text-sm text-stone-500 mt-1">Due: {task.due_date}</p>}
              {task.notes && <p className="text-sm text-stone-600 mt-2">{task.notes}</p>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => toggleTask(task)} className="p-2 text-stone-400 hover:text-green-600"><Check className="h-4 w-4" /></button>
              <button onClick={() => editTask(task)} className="p-2 text-stone-400 hover:text-[#5C0120]"><Edit2 className="h-4 w-4" /></button>
              <button onClick={() => deleteTask(task.id)} className="p-2 text-stone-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Favorites Comparison
function FavoritesComparison({ token, user }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  useEffect(() => {
    loadFavorites();
  }, []);
  
  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (err) {
      console.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };
  
  const categories = ['all', ...new Set(favorites.map(v => v.category))];
  const filteredFavorites = selectedCategory === 'all' 
    ? favorites 
    : favorites.filter(v => v.category === selectedCategory);
  
  // Group by category for comparison
  const groupedByCategory = favorites.reduce((acc, v) => {
    if (!acc[v.category]) acc[v.category] = [];
    acc[v.category].push(v);
    return acc;
  }, {});
  
  return (
    <div className="space-y-6">
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        </div>
      ) : favorites.length === 0 ? (
        <div className="bg-white rounded-sm border border-stone-200 p-12 text-center">
          <Heart className="h-16 w-16 mx-auto mb-4 text-stone-300" />
          <h3 className="text-xl font-semibold mb-2">No favourites yet</h3>
          <p className="text-stone-500 mb-4">Browse vendors and click the heart icon to save your favourites for comparison.</p>
          <Button onClick={() => window.location.href = '/vendors'} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90">
            Browse Vendors
          </Button>
        </div>
      ) : (
        <>
          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  selectedCategory === cat
                    ? 'bg-[#5C0120] text-white'
                    : 'bg-white border border-stone-200 text-stone-600 hover:border-[#5C0120]'
                }`}
              >
                {cat === 'all' ? 'All Categories' : cat}
              </button>
            ))}
          </div>
          
          {/* Comparison Grid */}
          {Object.entries(groupedByCategory)
            .filter(([cat]) => selectedCategory === 'all' || cat === selectedCategory)
            .map(([category, vendors]) => (
              <div key={category}>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#D4AF37] rounded-full"></span>
                  {category} ({vendors.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vendors.map((vendor) => (
                    <div key={vendor.id} className="bg-white rounded-sm border border-stone-200 overflow-hidden">
                      {vendor.images?.[0] && (
                        <div className="aspect-video bg-stone-100">
                          <img src={vendor.images[0]} alt={vendor.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          {vendor.logo && (
                            <img src={vendor.logo} alt="" className="w-8 h-8 object-contain" />
                          )}
                          <h4 className="font-semibold">{vendor.name}</h4>
                        </div>
                        <p className="text-sm text-stone-600 mb-3 whitespace-pre-line break-words line-clamp-3" data-testid={`planner-vendor-description-${vendor.id}`}>{vendor.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[#D4AF37] font-medium">{vendor.price_range}</span>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.location.href = `/vendors/${vendor.slug || vendor.id}`}
                          >
                            View <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </>
      )}
    </div>
  );
}

// Messages Section - User Inbox
function MessagesSection({ token, onUnreadChange }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedVendorProfile, setSelectedVendorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    const loadVendorProfile = async () => {
      if (!selectedConversation?.vendor_id) {
        setSelectedVendorProfile(null);
        return;
      }

      try {
        const response = await axios.get(`${API}/vendors/${selectedConversation.vendor_id}`);
        setSelectedVendorProfile(response.data);
      } catch (err) {
        setSelectedVendorProfile(null);
      }
    };

    loadVendorProfile();
  }, [selectedConversation?.vendor_id]);

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
      onUnreadChange?.(response.data.filter((conversation) => conversation.status === 'replied').length);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedConversation(response.data);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/messages/${selectedConversation.conversation_id}/reply`,
        { message: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConversation(response.data);
      setReplyText('');
      loadConversations(); // Refresh list
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'replied':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Replied</span>;
      case 'read':
        return <span className="px-2 py-1 text-xs bg-[#D4AF37]/15 text-[#5C0120] rounded-full">Read</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-stone-100 text-stone-600 rounded-full">Sent</span>;
    }
  };

  const vendorEmailHref = selectedVendorProfile ? buildVendorEmailHref(selectedVendorProfile.email, selectedVendorProfile.name) : '';
  const vendorWhatsAppHref = selectedVendorProfile ? buildVendorWhatsAppHref(selectedVendorProfile.whatsapp || '') : '';
  const vendorDirectLinks = selectedVendorProfile ? [
    selectedVendorProfile.website ? { label: 'Website', href: buildSocialLink('website', selectedVendorProfile.website) } : null,
    selectedVendorProfile.social_links?.instagram ? { label: 'Instagram', href: buildSocialLink('instagram', selectedVendorProfile.social_links.instagram) } : null,
    selectedVendorProfile.social_links?.facebook ? { label: 'Facebook', href: buildSocialLink('facebook', selectedVendorProfile.social_links.facebook) } : null,
  ].filter(Boolean) : [];

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-stone-200 p-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37] mx-auto" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[500px]">
        {/* Conversations List */}
        <div className="border-r border-stone-200">
          <div className="p-4 border-b border-stone-200 bg-stone-50">
            <h3 className="font-semibold flex items-center gap-2">
              <Inbox className="h-5 w-5 text-[#5C0120]" />
              Your Messages
            </h3>
            <p className="text-xs text-stone-500 mt-1">{conversations.length} conversation(s)</p>
          </div>
          
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-stone-500">
              <Inbox className="h-12 w-12 mx-auto mb-3 text-stone-300" />
              <p className="text-sm">No messages yet</p>
              <p className="text-xs mt-1">Contact vendors to start conversations</p>
              <Link to="/vendors">
                <Button variant="outline" size="sm" className="mt-4">
                  Browse Vendors
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-stone-100 max-h-[400px] overflow-y-auto">
              {conversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => loadConversation(conv.conversation_id)}
                  className={`w-full p-4 text-left hover:bg-stone-50 transition-colors ${
                    selectedConversation?.conversation_id === conv.conversation_id ? 'bg-[#5C0120]/5 border-l-4 border-[#5C0120]' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{conv.vendor_name}</p>
                      <p className="text-xs text-stone-500 truncate">{conv.subject}</p>
                    </div>
                    {getStatusBadge(conv.status)}
                  </div>
                  <p className="text-xs text-stone-400 mt-2">{formatDate(conv.updated_at || conv.created_at)}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conversation Detail */}
        <div className="lg:col-span-2 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Conversation Header */}
              <div className="p-4 border-b border-stone-200 bg-stone-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{selectedConversation.vendor_name}</h4>
                    <p className="text-sm text-stone-500">{selectedConversation.subject}</p>
                  </div>
                  <Link to={`/vendors/${selectedConversation.vendor_id}`}>
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4 mr-1" />
                      View Vendor
                    </Button>
                  </Link>
                </div>
                {selectedConversation.wedding_date && (
                  <p className="text-xs text-stone-500 mt-2">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Wedding Date: {selectedConversation.wedding_date}
                  </p>
                )}
                {(vendorEmailHref || vendorWhatsAppHref || vendorDirectLinks.length > 0) && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="planner-conversation-direct-links">
                    {vendorEmailHref && (
                      <a href={vendorEmailHref} className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-sm text-stone-700 hover:border-[#5C0120] hover:text-[#5C0120]" data-testid="planner-conversation-email-link">
                        Email Vendor
                      </a>
                    )}
                    {vendorWhatsAppHref && (
                      <a href={vendorWhatsAppHref} target="_blank" rel="noopener noreferrer" className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-sm text-stone-700 hover:border-[#25D366] hover:text-[#25D366]" data-testid="planner-conversation-whatsapp-link">
                        WhatsApp Vendor
                      </a>
                    )}
                    {vendorDirectLinks.map((link) => (
                      <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-sm text-stone-700 hover:border-[#5C0120] hover:text-[#5C0120]" data-testid={`planner-conversation-${link.label.toLowerCase()}-link`}>
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[300px]">
                {selectedConversation.messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg.sender === 'user'
                          ? 'bg-[#5C0120] text-white'
                          : 'bg-stone-100 text-stone-800'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-xs mt-2 ${msg.sender === 'user' ? 'text-white/70' : 'text-stone-400'}`}>
                        {msg.sender_name} • {formatDate(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t border-stone-200 bg-stone-50">
                <div className="flex gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                  />
                  <Button 
                    onClick={sendReply} 
                    disabled={!replyText.trim() || sending}
                    className="bg-[#D4AF37] hover:bg-[#D4AF37]/90"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-400">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 mx-auto mb-3" />
                <p>Select a conversation to view</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Account Section - Password Change
function AccountSection({ token, user, forcePasswordChangeRequired, onPasswordChanged }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      if (onPasswordChanged) {
        await onPasswordChanged();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow max-w-md">
      <div className="flex items-center gap-3 mb-6">
        <Lock className="h-6 w-6 text-[#5C0120]" />
        <h2 className="text-xl font-semibold">Change Password</h2>
      </div>

      {forcePasswordChangeRequired && (
        <div className="mb-4 rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="planner-account-force-password-change-note">
          Your password was reset by admin. Please set a new password now to continue.
        </div>
      )}

      <form onSubmit={handlePasswordChange} className="space-y-4">
        <div>
          <Label htmlFor="current-password">Current Password</Label>
          <Input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            className="mt-1"
            data-testid="current-password-input"
          />
        </div>

        <div>
          <Label htmlFor="new-password">New Password</Label>
          <Input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
            className="mt-1"
            data-testid="new-password-input"
          />
        </div>

        <div>
          <Label htmlFor="confirm-password">Confirm New Password</Label>
          <Input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="mt-1"
            data-testid="confirm-password-input"
          />
        </div>

        {message.text && (
          <div className={`p-3 rounded-sm text-sm ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        <Button 
          type="submit" 
          disabled={loading}
          className="w-full bg-[#5C0120] hover:bg-[#5C0120]/90"
          data-testid="change-password-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Change Password
        </Button>
      </form>
    </div>
  );
}
